import { Component, inject, Input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormGroup } from '@angular/forms';
import { OrderProtectionFormShape } from '../../smart-cart-form/widget-editors/order-protection/order-protection.model';
import { ORDER_PROTECTION_ICON_SVGS } from './order-protection-icon-svgs';

// Sample cart used only to render the focused Order Protection preview (matches the Figma mock).
const SAMPLE_SUBTOTAL = 72;
const SAMPLE_ITEM_COUNT = 2;
const TOGGLE_KNOB_ON = 22;
const TOGGLE_KNOB_OFF = 2;

@Component({
  selector: 'order-protection-preview',
  templateUrl: './order-protection-preview.component.html',
  styleUrls: ['./order-protection-preview.component.scss'],
  standalone: false,
})
export class OrderProtectionPreviewComponent {
  @Input({ required: true }) formGroup!: FormGroup<OrderProtectionFormShape>;

  // Hover state lets the preview show the configured hover colors on mouse-over.
  quickHover = false;
  continueHover = false;
  readonly toggleKnobOn = TOGGLE_KNOB_ON;
  readonly toggleKnobOff = TOGGLE_KNOB_OFF;

  private readonly sanitizer = inject(DomSanitizer);

  get controls(): OrderProtectionFormShape {
    return this.formGroup.controls;
  }

  get selectingType(): string {
    return this.controls.selectingMethod.controls.type.value;
  }
  get isToggle(): boolean {
    return this.selectingType === 'toggle';
  }
  get isCheckbox(): boolean {
    return this.selectingType === 'checkbox';
  }
  get isQuickCheckout(): boolean {
    return this.selectingType === 'quick_checkout';
  }
  // The preview's selected state mirrors the Auto-selected setting (pre-selected vs opt-in).
  get isSelected(): boolean {
    return this.controls.selectingMethod.controls.autoSelected.value;
  }

  get title(): string {
    return this.controls.title.value || 'Order protection';
  }
  get subtitleColor(): string {
    return this.controls.subtitleColor.value;
  }

  // Resolved once so the border and the padding can't disagree — a zero width draws nothing, so it has to read
  // as "no border" for both, matching the storefront.
  private get borderWidth(): number {
    const border = this.controls.border.value;
    return border.enabled && border.width > 0 ? border.width : 0;
  }

  get cardBorder(): string {
    return this.borderWidth > 0 ? `${this.borderWidth}px solid ${this.controls.border.value.color}` : 'none';
  }

  get cardPadding(): string {
    return this.borderWidth > 0 ? 'var(--padding-12)' : 'var(--padding-12) 0';
  }

  get cardBorderRadius(): string {
    return `${this.controls.border.value.radius}px`;
  }

  get subtitle(): string {
    return this.controls.subtitle.value;
  }
  get imageUrl(): string {
    return this.controls.productImageBase64.value;
  }
  get hasInfo(): boolean {
    const info = this.controls.info.controls;
    return info.type.value === 'modal' || !!info.tooltipContent.value;
  }

  get selectorColor(): string {
    const method = this.controls.selectingMethod.controls;
    const colors = this.isCheckbox ? method.checkbox.controls : method.toggle.controls;
    return this.isSelected ? colors.selectedColor.value : colors.defaultColor.value;
  }

  get formattedPrice(): string {
    if (this.controls.pricingMethod.value === 'percent') {
      const percentage = this.controls.percent.controls.cartPercentage.value || 0;
      const price = (SAMPLE_SUBTOTAL * percentage) / 100;
      return price > 0 ? `$${price.toFixed(2)}` : 'Free';
    }
    const flat = this.controls.flatPrice.value || 0;
    return flat > 0 ? `$${flat.toFixed(2)}` : 'Free';
  }

  get itemCountLabel(): string {
    return `Estimated Total (${SAMPLE_ITEM_COUNT} items)`;
  }
  get estimatedTotal(): string {
    return `$${SAMPLE_SUBTOTAL}`;
  }

  get quickButtonLabel(): string {
    return this.controls.quickCheckout.controls.buttonLabel.value || 'Checkout';
  }
  get quickIconUrl(): string {
    return this.controls.quickCheckout.controls.icon.value;
  }
  get quickIconColor(): string {
    return this.controls.quickCheckout.controls.iconColor.value;
  }
  // Background follows the hover color while the pointer is over the button.
  get quickButtonStyle(): Record<string, string> {
    const quick = this.controls.quickCheckout.controls;
    return {
      'background-color': this.quickHover ? quick.hoverColor.value : quick.backgroundColor.value,
      color: quick.textColor.value,
      'border-radius': `${quick.cornerRadius.value ?? 0}px`,
    };
  }
  // Preset icons are inlined as SVG so the preview can tint them with any Icon Color (the CDN serves
  // them without CORS, so a remote <img> can't be recolored). Custom/unknown icons fall back to <img>.
  private get quickIconName(): string | null {
    const match = /\/icons\/([^/]+)-color\.svg(?:$|\?)/.exec(this.quickIconUrl);
    return match ? match[1] : null;
  }
  // bypassSecurityTrustHtml returns a fresh object every call and [innerHTML] compares by identity, so
  // trusting the SVG inside the getter made the binding change on every change-detection pass — Angular
  // re-parsed the icon and rebuilt that subtree each time. The icon set is static, so trust each one once.
  private readonly trustedIconSvgs = new Map<string, SafeHtml>();

  get quickIconSvg(): SafeHtml | null {
    const name = this.quickIconName;
    if (!name) return null;
    const cached = this.trustedIconSvgs.get(name);
    if (cached) return cached;
    const svg = ORDER_PROTECTION_ICON_SVGS[name];
    if (!svg) return null;
    const trusted = this.sanitizer.bypassSecurityTrustHtml(svg);
    this.trustedIconSvgs.set(name, trusted);
    return trusted;
  }

  get continueWithoutHtml(): string {
    return this.controls.quickCheckout.controls.continueWithout.controls.content.value;
  }
  // 'Continue without' link uses its Text Color, swapping to Hover Color on mouse-over.
  get continueStyle(): Record<string, string> {
    const link = this.controls.quickCheckout.controls.continueWithout.controls;
    return { color: this.continueHover ? link.hoverColor.value : link.textColor.value };
  }
}
